import type { MemoryOrigin } from "@/features/material-compliance/api/types"
import { declarationReviewPath } from "@/features/material-compliance/components/declarations/routes"
import { Link } from "react-router"

/**
 * The source half of a memory's trail, as a node — a real link when it safely can be one,
 * the bare reference otherwise. Shared by `MemoryTrail` (the collapsible detail) and
 * `MemoryBecause` (the always-visible glance line) so there is exactly one place that
 * decides when a source is linkable, never two that could disagree.
 *
 * ONLY A DECLARATION GETS A REAL LINK. A declaration has one canonical path
 * (`declarationReviewPath`); a questionnaire's path depends on its upload type (PDF vs.
 * Excel), which `origin` has no way to know, so linking it would risk a dead link more
 * often than it would help. Named-not-linked there is a known, deliberate gap — not an
 * oversight — until the read side carries enough to resolve it safely.
 */
export function renderMemorySource(
  origin: Pick<MemoryOrigin, "sourceType" | "sourceId" | "sourceRef">,
  openSourceLabel: string,
  testId: string,
): React.ReactNode {
  const sourceLink =
    origin.sourceType === "declaration" && origin.sourceId
      ? declarationReviewPath(origin.sourceId)
      : null

  if (!sourceLink) return origin.sourceRef ?? null

  return (
    <Link
      to={sourceLink}
      className="underline decoration-dotted underline-offset-2 hover:text-white"
      data-testid={`memory-trail-source-link-${testId}`}
    >
      {origin.sourceRef ?? openSourceLabel}
    </Link>
  )
}
